/**
 * AudioContext — global audio playback for HBCU Media.
 *
 * Wraps a single HTML5 Audio element shared across the whole app,
 * so the user can navigate anywhere and keep listening.
 *
 * track shape:
 *   { streamUrl, title, artist, album, imageUrl, type: 'radio'|'podcast' }
 */
import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';

const AudioCtx = createContext(null);
export const useAudio = () => useContext(AudioCtx);

export function AudioProvider({ children }) {
  const audioRef   = useRef(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying,    setIsPlaying]    = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);
  const [currentTime,  setCurrentTime]  = useState(0);
  const [duration,     setDuration]     = useState(0);
  const [error,        setError]        = useState(null);

  /* ── Bootstrap the Audio element once ── */
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const onPlaying    = () => { setIsPlaying(true); setIsLoading(false); setError(null); };
    const onPause      = () => setIsPlaying(false);
    const onWaiting    = () => setIsLoading(true);
    const onCanplay    = () => setIsLoading(false);
    const onTimeupdate = () => setCurrentTime(audio.currentTime);
    const onDuration   = () => setDuration(isFinite(audio.duration) ? audio.duration : 0);
    const onEnded      = () => { setIsPlaying(false); setCurrentTime(0); };
    const onError      = () => { setIsLoading(false); setIsPlaying(false); setError('Stream unavailable'); };

    audio.addEventListener('playing',        onPlaying);
    audio.addEventListener('pause',          onPause);
    audio.addEventListener('waiting',        onWaiting);
    audio.addEventListener('canplay',        onCanplay);
    audio.addEventListener('timeupdate',     onTimeupdate);
    audio.addEventListener('durationchange', onDuration);
    audio.addEventListener('ended',          onEnded);
    audio.addEventListener('error',          onError);

    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('playing',        onPlaying);
      audio.removeEventListener('pause',          onPause);
      audio.removeEventListener('waiting',        onWaiting);
      audio.removeEventListener('canplay',        onCanplay);
      audio.removeEventListener('timeupdate',     onTimeupdate);
      audio.removeEventListener('durationchange', onDuration);
      audio.removeEventListener('ended',          onEnded);
      audio.removeEventListener('error',          onError);
    };
  }, []);

  /* ── Play a new track ── */
  const playTrack = useCallback((track) => {
    const audio = audioRef.current;
    if (!audio) return;

    setCurrentTrack(track);
    setIsLoading(true);
    setCurrentTime(0);
    setDuration(0);
    setError(null);

    audio.src = track.streamUrl;
    audio.load();
    audio.play().catch((err) => {
      console.error('Playback error:', err);
      setIsLoading(false);
      setError('Could not start playback');
    });

    // Lock-screen / notification controls via MediaSession API
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title:  track.title,
        artist: track.artist || 'HBCU Media · GradLink',
        album:  track.album  || '',
        artwork: track.imageUrl
          ? [{ src: track.imageUrl, sizes: '512x512', type: 'image/jpeg' }]
          : [],
      });
      navigator.mediaSession.setActionHandler('play',  () => audio.play());
      navigator.mediaSession.setActionHandler('pause', () => audio.pause());

      if (track.type === 'podcast') {
        navigator.mediaSession.setActionHandler('seekbackward', () => {
          audio.currentTime = Math.max(0, audio.currentTime - 15);
        });
        navigator.mediaSession.setActionHandler('seekforward', () => {
          audio.currentTime += 30;
        });
      } else {
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler('seekforward',  null);
      }
    }
  }, []);

  /* ── Toggle play / pause ── */
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (isPlaying) audio.pause();
    else audio.play().catch(console.error);
  }, [isPlaying, currentTrack]);

  /* ── Seek (podcasts) ── */
  const seek = useCallback((time) => {
    if (audioRef.current) audioRef.current.currentTime = time;
  }, []);

  /* ── Stop & clear ── */
  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.src = ''; }
    setCurrentTrack(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
  }, []);

  return (
    <AudioCtx.Provider value={{
      currentTrack, isPlaying, isLoading, currentTime, duration, error,
      playTrack, togglePlay, seek, stop,
    }}>
      {children}
    </AudioCtx.Provider>
  );
}
